import React, { useState, useEffect } from 'react';
import { Cpu, CheckCircle, RefreshCw, AlertTriangle, Database, Download } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input, Select } from '../ui/Form';
import { Badge } from '../ui/Badge';
import { LLM_PROVIDER_CONFIGS, type LLMProvider } from '@data/llmProviders';
import { type VerifyStatus, verifyGemini, verifyClaude, verifyOpenAI, verifyXAI } from '@utils/llmVerification';
import { useUserSettings, type UserSettings } from '@hooks/useUserSettings';

export const ArchitectureEnvironment: React.FC = () => {
  const { settings, loading, save } = useUserSettings();

  const [provider, setProvider] = useState<LLMProvider>('gemini');
  const [localGeminiKey, setLocalGeminiKey] = useState('');
  const [localGeminiModel, setLocalGeminiModel] = useState('gemini-3-pro-preview');
  const [localClaudeKey, setLocalClaudeKey] = useState('');
  const [localClaudeModel, setLocalClaudeModel] = useState('claude-sonnet-4-5-20250929');
  const [localClaudeProxy, setLocalClaudeProxy] = useState('');
  const [geminiStatus, setGeminiStatus] = useState<VerifyStatus>('idle');
  const [claudeStatus, setClaudeStatus] = useState<VerifyStatus>('idle');
  const [localOpenAIKey, setLocalOpenAIKey] = useState('');
  const [localOpenAIModel, setLocalOpenAIModel] = useState('gpt-4o');
  const [openaiStatus, setOpenaiStatus] = useState<VerifyStatus>('idle');
  const [localXAIKey, setLocalXAIKey] = useState('');
  const [localXAIModel, setLocalXAIModel] = useState('grok-4-1-fast-reasoning');
  const [xaiStatus, setXaiStatus] = useState<VerifyStatus>('idle');
  const [geminiErrorMessage, setGeminiErrorMessage] = useState('');
  const [claudeErrorMessage, setClaudeErrorMessage] = useState('');
  const [openaiErrorMessage, setOpenaiErrorMessage] = useState('');
  const [xaiErrorMessage, setXaiErrorMessage] = useState('');

  const [dbKeyCount, setDbKeyCount] = useState(0);
  const [loadingFromDb, setLoadingFromDb] = useState(false);
  const [keysLoaded, setKeysLoaded] = useState(false);
  const [modelJustSaved, setModelJustSaved] = useState(false);

  // Track how many keys the DB has — updates when settings hook delivers data
  useEffect(() => {
    const keyFields: (keyof UserSettings)[] = ['gemini_api_key', 'anthropic_api_key', 'openai_api_key', 'xai_api_key'];
    setDbKeyCount(keyFields.filter(k => !!settings[k]).length);
  }, [settings]);

  function applySettings(s: UserSettings) {
    if (s.llm_provider) setProvider(s.llm_provider as LLMProvider);
    if (s.gemini_api_key) setLocalGeminiKey(s.gemini_api_key);
    if (s.gemini_model) setLocalGeminiModel(s.gemini_model);
    if (s.anthropic_api_key) setLocalClaudeKey(s.anthropic_api_key);
    if (s.claude_model) setLocalClaudeModel(s.claude_model);
    if (s.anthropic_proxy_url) setLocalClaudeProxy(s.anthropic_proxy_url);
    if (s.openai_api_key) setLocalOpenAIKey(s.openai_api_key);
    if (s.openai_model) setLocalOpenAIModel(s.openai_model);
    if (s.xai_api_key) setLocalXAIKey(s.xai_api_key);
    if (s.xai_model) setLocalXAIModel(s.xai_model);
  }

  // Manual load: fetch from DB, apply to fields, verify each one
  const handleLoadFromDb = async () => {
    setLoadingFromDb(true);
    try {
      // Apply whatever the hook already has
      applySettings(settings);
      setKeysLoaded(true);

      // Verify each provider that has a key
      if (settings.gemini_api_key) {
        setGeminiStatus('verifying');
        const r = await verifyGemini(settings.gemini_api_key, settings.gemini_model || 'gemini-3-pro-preview');
        setGeminiStatus(r.status);
        if (r.status === 'error') setGeminiErrorMessage(r.errorMessage || '');
      }
      if (settings.anthropic_api_key) {
        setClaudeStatus('verifying');
        const r = await verifyClaude(
          settings.anthropic_api_key,
          settings.claude_model || 'claude-sonnet-4-5-20250929',
          settings.anthropic_proxy_url || import.meta.env.VITE_ANTHROPIC_PROXY_URL || 'https://anthropic-proxy.frobinson47.workers.dev'
        );
        setClaudeStatus(r.status);
        if (r.status === 'error') setClaudeErrorMessage(r.errorMessage || '');
      }
      if (settings.openai_api_key) {
        setOpenaiStatus('verifying');
        const r = await verifyOpenAI(settings.openai_api_key, settings.openai_model || 'gpt-4o');
        setOpenaiStatus(r.status);
        if (r.status === 'error') setOpenaiErrorMessage(r.errorMessage || '');
      }
      if (settings.xai_api_key) {
        setXaiStatus('verifying');
        const r = await verifyXAI(settings.xai_api_key, settings.xai_model || 'grok-4-1-fast-reasoning');
        setXaiStatus(r.status);
        if (r.status === 'error') setXaiErrorMessage(r.errorMessage || '');
      }
    } finally {
      setLoadingFromDb(false);
    }
  };

  // Pull model option arrays from shared config
  const geminiModelOptions = LLM_PROVIDER_CONFIGS.find(p => p.id === 'gemini')!.models;
  const claudeModelOptions = LLM_PROVIDER_CONFIGS.find(p => p.id === 'claude')!.models;
  const openaiModelOptions = LLM_PROVIDER_CONFIGS.find(p => p.id === 'openai')!.models;
  const xaiModelOptions = LLM_PROVIDER_CONFIGS.find(p => p.id === 'xai')!.models;

  const handleVerifyGemini = async () => {
    if (!localGeminiKey) return;
    setGeminiStatus('verifying');
    setGeminiErrorMessage('');
    const result = await verifyGemini(localGeminiKey, localGeminiModel);
    if (result.status === 'success') {
      setGeminiStatus('success');
      save({ gemini_api_key: localGeminiKey, gemini_model: localGeminiModel });
    } else {
      setGeminiStatus('error');
      setGeminiErrorMessage(result.errorMessage || 'Gemini authentication failed.');
    }
  };

  const handleVerifyClaude = async () => {
    if (!localClaudeKey) return;
    setClaudeStatus('verifying');
    setClaudeErrorMessage('');
    const result = await verifyClaude(localClaudeKey, localClaudeModel, localClaudeProxy);
    if (result.status === 'success') {
      setClaudeStatus('success');
      save({ anthropic_api_key: localClaudeKey, claude_model: localClaudeModel, anthropic_proxy_url: localClaudeProxy });
    } else {
      setClaudeStatus('error');
      setClaudeErrorMessage(result.errorMessage || 'Claude authentication failed.');
    }
  };

  const handleVerifyOpenAI = async () => {
    if (!localOpenAIKey) return;
    setOpenaiStatus('verifying');
    setOpenaiErrorMessage('');
    const result = await verifyOpenAI(localOpenAIKey, localOpenAIModel);
    if (result.status === 'success') {
      setOpenaiStatus('success');
      save({ openai_api_key: localOpenAIKey, openai_model: localOpenAIModel });
    } else {
      setOpenaiStatus('error');
      setOpenaiErrorMessage(result.errorMessage || 'OpenAI authentication failed.');
    }
  };

  const handleVerifyXAI = async () => {
    if (!localXAIKey) return;
    setXaiStatus('verifying');
    setXaiErrorMessage('');
    const result = await verifyXAI(localXAIKey, localXAIModel);
    if (result.status === 'success') {
      setXaiStatus('success');
      save({ xai_api_key: localXAIKey, xai_model: localXAIModel });
    } else {
      setXaiStatus('error');
      setXaiErrorMessage(result.errorMessage || 'xAI authentication failed.');
    }
  };

  const handleProviderChange = (newProvider: LLMProvider) => {
    setProvider(newProvider);
    save({ llm_provider: newProvider });
  };

  function renderStatusBadge(status: VerifyStatus): React.ReactNode {
    switch (status) {
      case 'success':
        return (
          <Badge variant="success-muted" size="sm" leftIcon={<CheckCircle size={12} />}>
            Architect Verified
          </Badge>
        );
      case 'verifying':
        return (
          <Badge variant="default-muted" size="sm" leftIcon={<RefreshCw size={12} className="animate-spin" />}>
            Verifying...
          </Badge>
        );
      case 'error':
        return (
          <Badge variant="danger-muted" size="sm" leftIcon={<AlertTriangle size={12} />}>
            Verification Failed
          </Badge>
        );
      default:
        return null;
    }
  }

  /** Mask an API key for display: ••••••••abcd */
  function maskKey(key: string): string {
    if (key.length <= 4) return '••••';
    return '•'.repeat(Math.min(key.length - 4, 20)) + key.slice(-4);
  }

  /** Render a provider config panel — shows vault-loaded masked key or manual entry */
  function renderProviderConfig(id: LLMProvider, opts: {
    key: string; setKey: (v: string) => void;
    model: string; setModel: (v: string) => void;
    modelOptions: { value: string; label: string }[];
    status: VerifyStatus; errorMessage: string;
    onVerify: () => void;
    vaultKey?: string; vaultModel?: string; settingsModelKey: keyof UserSettings;
    description: string; keyPlaceholder: string;
    accentColor: string;
    extraFields?: React.ReactNode;
  }) {
    if (provider !== id) return null;
    const hasVaultKey = keysLoaded && !!opts.vaultKey;

    // The key can be vault-loaded (masked, read-only) while the model is
    // still just a plain preference field -- changing it shouldn't require
    // re-entering or re-verifying the key. When the key is vault-loaded,
    // save the model choice straight to DB on change instead of routing
    // through the Verify Architect flow (which is key-driven and hidden
    // in this state).
    const handleModelChange = (value: string) => {
      opts.setModel(value);
      if (hasVaultKey) {
        void save({ [opts.settingsModelKey]: value }).then((ok) => {
          if (ok) {
            setModelJustSaved(true);
            setTimeout(() => setModelJustSaved(false), 2000);
          }
        });
      }
    };

    return (
      <div className="space-y-4 animate-in fade-in">
        <div className="text-[11px] text-slate-400 leading-relaxed">
          <p>{opts.description}</p>
        </div>

        <div className="space-y-1.5">
          <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest ml-1">API Key</label>
          {hasVaultKey ? (
            <div className={`flex items-center justify-between bg-[var(--color-slate-950)] rounded-md px-3 py-2.5 border border-${opts.accentColor}-500/30`}>
              <span className="text-sm text-slate-400 font-mono tracking-wider">{maskKey(opts.vaultKey!)}</span>
              <Badge variant="success-muted" size="xs" leftIcon={<CheckCircle size={10} />}>Vault</Badge>
            </div>
          ) : (
            <Input
              type="password"
              value={opts.key}
              onChange={(e) => opts.setKey(e.target.value)}
              placeholder={opts.keyPlaceholder}
              size="md"
              className={`bg-[var(--color-slate-950)] border-slate-800 focus:border-${opts.accentColor}-500`}
            />
          )}
        </div>

        {opts.extraFields}

        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest ml-1">Model</label>
            {hasVaultKey && modelJustSaved && (
              <Badge variant="success-muted" size="xs" leftIcon={<CheckCircle size={10} />}>Saved</Badge>
            )}
          </div>
          <Select
            value={hasVaultKey ? (opts.vaultModel || opts.model) : opts.model}
            onChange={(e) => handleModelChange(e.target.value)}
            options={opts.modelOptions}
            size="md"
            className={`bg-[var(--color-slate-950)] border-slate-800 focus:border-${opts.accentColor}-500`}
          />
        </div>

        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-2">
            {renderStatusBadge(opts.status)}
          </div>
          {!hasVaultKey && (
            <Button
              variant="secondary"
              size="sm"
              onClick={opts.onVerify}
              disabled={!opts.key}
              isLoading={opts.status === 'verifying'}
              loadingText="Verifying..."
            >
              Verify Architect
            </Button>
          )}
        </div>
        {opts.status === 'error' && <p className="text-[11px] text-red-400 font-bold px-1 italic">{opts.errorMessage}</p>}
      </div>
    );
  }

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2 text-blue-400">
        <Cpu size={18} />
        <h3 className="text-sm font-black uppercase tracking-widest">Architecture Environment</h3>
      </div>

      {/* DB Key Vault Status */}
      {!keysLoaded && dbKeyCount > 0 && (
        <div className="flex items-center justify-between bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-4 py-3">
          <div className="flex items-center gap-2">
            <Database size={16} className="text-emerald-400" />
            <span className="text-sm text-emerald-300 font-medium">
              {dbKeyCount} API key{dbKeyCount !== 1 ? 's' : ''} available in secure vault
            </span>
          </div>
          <Button
            variant="primary"
            size="sm"
            leftIcon={loadingFromDb ? <RefreshCw size={14} className="animate-spin" /> : <Download size={14} />}
            onClick={handleLoadFromDb}
            disabled={loadingFromDb}
            isLoading={loadingFromDb}
            loadingText="Loading & verifying..."
          >
            Load Keys from Vault
          </Button>
        </div>
      )}
      {keysLoaded && dbKeyCount > 0 && (
        <div className="flex items-center gap-2 bg-emerald-500/5 border border-emerald-500/20 rounded-lg px-4 py-2">
          <CheckCircle size={14} className="text-emerald-400" />
          <span className="text-xs text-emerald-400">{dbKeyCount} keys loaded from secure vault</span>
        </div>
      )}
      {loading && dbKeyCount === 0 && (
        <div className="flex items-center gap-2 bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-2">
          <RefreshCw size={14} className="animate-spin text-slate-400" />
          <span className="text-xs text-slate-400">Checking secure vault...</span>
        </div>
      )}

      <div className="bg-[var(--color-slate-900)] border-l-4 border-l-accent rounded-lg p-5 space-y-4 shadow-xl">
        {/* Provider Nav Bar */}
        <div className="space-y-2 pb-4 border-b border-slate-800">
          <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">LLM Provider</label>
          <nav className="grid grid-cols-4 gap-2">
            {/* Google Gemini */}
            <button
              onClick={() => handleProviderChange('gemini')}
              className={`relative flex items-center gap-2.5 px-3 py-2.5 rounded-lg border transition-all duration-200 cursor-pointer ${
                provider === 'gemini'
                  ? 'bg-blue-500/10 border-blue-500/50 shadow-[0_0_12px_rgba(59,130,246,0.3)]'
                  : 'bg-slate-900/50 border-slate-700/50 hover:border-slate-600 hover:bg-slate-800/50'
              }`}
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4 flex-shrink-0" fill="none">
                <path d="M12 24C12 22.3431 11.6831 20.7013 11.0667 19.1667C10.4503 17.632 9.5474 16.234 8.3935 15.0416C7.2396 13.8492 5.8491 12.8861 4.3138 12.2072C2.7784 11.5284 1.125 11.149 0 11.0333V12.9667C1.125 12.851 2.7784 12.4716 4.3138 11.7928C5.8491 11.1139 7.2396 10.1508 8.3935 8.9584C9.5474 7.766 10.4503 6.368 11.0667 4.8333C11.6831 3.2987 12 1.6569 12 0C12 1.6569 12.3169 3.2987 12.9333 4.8333C13.5497 6.368 14.4526 7.766 15.6065 8.9584C16.7604 10.1508 18.1509 11.1139 19.6862 11.7928C21.2216 12.4716 22.875 12.851 24 12.9667V11.0333C22.875 11.149 21.2216 11.5284 19.6862 12.2072C18.1509 12.8861 16.7604 13.8492 15.6065 15.0416C14.4526 16.234 13.5497 17.632 12.9333 19.1667C12.3169 20.7013 12 22.3431 12 24Z" fill={provider === 'gemini' ? '#60a5fa' : 'rgb(var(--color-slate-500))'}/>
              </svg>
              <span className={`text-[11px] font-black uppercase tracking-widest ${provider === 'gemini' ? 'text-blue-300' : 'text-slate-400'}`}>Gemini</span>
              <span className={`ml-auto w-2 h-2 rounded-full ${provider === 'gemini' ? 'bg-blue-400 shadow-[0_0_6px_rgba(96,165,250,0.8)]' : 'bg-slate-600'}`} />
            </button>

            {/* Anthropic Claude */}
            <button
              onClick={() => handleProviderChange('claude')}
              className={`relative flex items-center gap-2.5 px-3 py-2.5 rounded-lg border transition-all duration-200 cursor-pointer ${
                provider === 'claude'
                  ? 'bg-orange-500/10 border-orange-500/50 shadow-[0_0_12px_rgba(249,115,22,0.3)]'
                  : 'bg-slate-900/50 border-slate-700/50 hover:border-slate-600 hover:bg-slate-800/50'
              }`}
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4 flex-shrink-0" fill="none">
                <path d="M17.3541 2L9.56386 21.9097L7.0997 15.4563L0.646484 12.9918L17.3541 2Z" fill={provider === 'claude' ? '#fb923c' : 'rgb(var(--color-slate-500))'}/>
                <path d="M17.3541 2L9.56386 21.9097L12.028 15.4563L23.3541 12.9918L17.3541 2Z" fill={provider === 'claude' ? '#f97316' : 'rgb(var(--color-slate-600))'}/>
              </svg>
              <span className={`text-[11px] font-black uppercase tracking-widest ${provider === 'claude' ? 'text-orange-300' : 'text-slate-400'}`}>Claude</span>
              <span className={`ml-auto w-2 h-2 rounded-full ${provider === 'claude' ? 'bg-orange-400 shadow-[0_0_6px_rgba(251,146,60,0.8)]' : 'bg-slate-600'}`} />
            </button>

            {/* OpenAI */}
            <button
              onClick={() => handleProviderChange('openai')}
              className={`relative flex items-center gap-2.5 px-3 py-2.5 rounded-lg border transition-all duration-200 cursor-pointer ${
                provider === 'openai'
                  ? 'bg-green-500/10 border-green-500/50 shadow-[0_0_12px_rgba(34,197,94,0.3)]'
                  : 'bg-slate-900/50 border-slate-700/50 hover:border-slate-600 hover:bg-slate-800/50'
              }`}
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4 flex-shrink-0" fill={provider === 'openai' ? '#4ade80' : 'rgb(var(--color-slate-500))'}>
                <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.998 5.998 0 0 0-3.998 2.9 6.042 6.042 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/>
              </svg>
              <span className={`text-[11px] font-black uppercase tracking-widest ${provider === 'openai' ? 'text-green-300' : 'text-slate-400'}`}>OpenAI</span>
              <span className={`ml-auto w-2 h-2 rounded-full ${provider === 'openai' ? 'bg-green-400 shadow-[0_0_6px_rgba(74,222,128,0.8)]' : 'bg-slate-600'}`} />
            </button>

            {/* xAI Grok */}
            <button
              onClick={() => handleProviderChange('xai')}
              className={`relative flex items-center gap-2.5 px-3 py-2.5 rounded-lg border transition-all duration-200 cursor-pointer ${
                provider === 'xai'
                  ? 'bg-red-500/10 border-red-500/50 shadow-[0_0_12px_rgba(239,68,68,0.3)]'
                  : 'bg-slate-900/50 border-slate-700/50 hover:border-slate-600 hover:bg-slate-800/50'
              }`}
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4 flex-shrink-0" fill={provider === 'xai' ? '#f87171' : 'rgb(var(--color-slate-500))'}>
                <path d="M13.982 10.622L20.54 3h-1.554l-5.693 6.618L8.745 3H3.5l6.876 10.007L3.5 21h1.554l6.012-6.989L15.868 21H21.1l-7.118-10.378zm-2.128 2.474l-.697-.997L5.654 4.16h2.386l4.474 6.4.697.996 5.815 8.318h-2.387l-4.745-6.778z"/>
              </svg>
              <span className={`text-[11px] font-black uppercase tracking-widest ${provider === 'xai' ? 'text-red-300' : 'text-slate-400'}`}>xAI</span>
              <span className={`ml-auto w-2 h-2 rounded-full ${provider === 'xai' ? 'bg-red-400 shadow-[0_0_6px_rgba(248,113,113,0.8)]' : 'bg-slate-600'}`} />
            </button>
          </nav>
        </div>

        {/* Provider Configuration — vault-loaded or manual entry */}
        {renderProviderConfig('gemini', {
          key: localGeminiKey, setKey: setLocalGeminiKey,
          model: localGeminiModel, setModel: setLocalGeminiModel,
          modelOptions: geminiModelOptions,
          status: geminiStatus, errorMessage: geminiErrorMessage,
          onVerify: handleVerifyGemini,
          vaultKey: settings.gemini_api_key,
          vaultModel: settings.gemini_model,
          settingsModelKey: 'gemini_model',
          description: 'The Hookhouse Pro engine requires a valid Gemini API key for session synthesis.',
          keyPlaceholder: 'Paste your Google Cloud API key here...',
          accentColor: 'blue',
        })}
        {renderProviderConfig('claude', {
          key: localClaudeKey, setKey: setLocalClaudeKey,
          model: localClaudeModel, setModel: setLocalClaudeModel,
          modelOptions: claudeModelOptions,
          status: claudeStatus, errorMessage: claudeErrorMessage,
          onVerify: handleVerifyClaude,
          vaultKey: settings.anthropic_api_key,
          vaultModel: settings.claude_model,
          settingsModelKey: 'claude_model',
          description: 'Configure Anthropic Claude API for LLM operations. Requires proxy to bypass CORS.',
          keyPlaceholder: 'sk-ant-api03-...',
          accentColor: 'orange',
          extraFields: (
            <>
              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest ml-1">Proxy URL (CORS Bypass)</label>
                {keysLoaded && settings.anthropic_proxy_url ? (
                  <div className="text-xs text-slate-400 bg-[var(--color-slate-950)] rounded-md px-3 py-2.5 border border-slate-800 font-mono">{settings.anthropic_proxy_url}</div>
                ) : (
                  <Input type="text" value={localClaudeProxy} onChange={(e) => setLocalClaudeProxy(e.target.value)} placeholder="https://anthropic-proxy.frobinson47.workers.dev" size="md" className="bg-[var(--color-slate-950)] border-slate-800 focus:border-orange-500" />
                )}
              </div>
            </>
          ),
        })}
        {renderProviderConfig('openai', {
          key: localOpenAIKey, setKey: setLocalOpenAIKey,
          model: localOpenAIModel, setModel: setLocalOpenAIModel,
          modelOptions: openaiModelOptions,
          status: openaiStatus, errorMessage: openaiErrorMessage,
          onVerify: handleVerifyOpenAI,
          vaultKey: settings.openai_api_key,
          vaultModel: settings.openai_model,
          settingsModelKey: 'openai_model',
          description: 'Configure OpenAI API for LLM operations. No proxy required — calls go direct from browser.',
          keyPlaceholder: 'sk-proj-...',
          accentColor: 'green',
        })}

        {renderProviderConfig('xai', {
          key: localXAIKey, setKey: setLocalXAIKey,
          model: localXAIModel, setModel: setLocalXAIModel,
          modelOptions: xaiModelOptions,
          status: xaiStatus, errorMessage: xaiErrorMessage,
          onVerify: handleVerifyXAI,
          vaultKey: settings.xai_api_key,
          vaultModel: settings.xai_model,
          settingsModelKey: 'xai_model',
          description: 'Configure xAI Grok API for LLM operations. No proxy required — calls go direct from browser.',
          keyPlaceholder: 'xai-...',
          accentColor: 'red',
        })}
      </div>
    </section>
  );
};
